import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  Mail, Send, Plus, Edit, Eye, Trash2, RotateCcw, X, Loader2, Zap, LayoutTemplate,
  History, AlertTriangle, UserPlus, KeyRound, BellRing, ShieldCheck, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#C6A15B]/60 focus:outline-none";
const labelCls = "block text-xs font-medium text-slate-400 mb-1.5";
const Skeleton = ({ className = '' }) => <div className={`animate-pulse bg-white/[0.05] rounded-lg ${className}`} />;

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }], ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }], [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }], ['blockquote', 'code-block'], ['link', 'image'], ['clean'],
  ],
};
const quillFormats = ['header', 'bold', 'italic', 'underline', 'strike', 'color', 'background', 'list', 'align', 'blockquote', 'code-block', 'link', 'image'];

const VARIABLE_META = {
  headline: { label: 'Main Headline', placeholder: 'e.g. Big News from TC Protection Film', kind: 'text' },
  body_html: { label: 'Email Body', placeholder: 'Write your message here…', kind: 'html' },
  cta_label: { label: 'Button Text', placeholder: 'e.g. View Details', kind: 'text' },
  cta_url: { label: 'Button Link (URL)', placeholder: 'https://tcfilmprotection.com/…', kind: 'url' },
  update_title: { label: 'Update Title', placeholder: 'e.g. New Feature Released', kind: 'text' },
  update_body: { label: 'Update Details', placeholder: 'Explain the update…', kind: 'html' },
  when: { label: 'When', placeholder: 'e.g. Monday, March 3 · 8pm EST', kind: 'text' },
  impact: { label: 'Impact / Who this affects', placeholder: 'e.g. All dealers', kind: 'text' },
  expired_count: { label: 'Expired accounts', kind: 'number' },
  urgent_count: { label: 'Urgent (≤7d) accounts', kind: 'number' },
  warning_count: { label: 'Warning (≤30d) accounts', kind: 'number' },
  total_flagged: { label: 'Total flagged', kind: 'number' },
  today: { label: 'Today (date)', kind: 'text' },
  accounts_html: { label: 'Accounts List (rich text)', kind: 'html' },
  vehicle_year: { label: 'Vehicle year', kind: 'text' }, vehicle_make: { label: 'Vehicle make', kind: 'text' },
  vehicle_model: { label: 'Vehicle model', kind: 'text' }, vehicle_vin: { label: 'VIN', kind: 'text' },
  dealer_name: { label: 'Dealer name', kind: 'text' }, client_name: { label: 'Client name', kind: 'text' },
  warranty_id: { label: 'Warranty ID', kind: 'text' }, serial_number: { label: 'Serial number', kind: 'text' },
  install_date: { label: 'Install date', kind: 'text' }, warranty_end: { label: 'Warranty end date', kind: 'text' },
};
const AUTO_FILLED_VARS = new Set(['first_name', 'full_name', 'recipient_email', 'recipient_role']);
const getVarLabel = (name, fb) => VARIABLE_META[name]?.label || fb || name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const getVarPlaceholder = (name, fb) => VARIABLE_META[name]?.placeholder || fb || '';
const isHtmlVar = (name) => VARIABLE_META[name]?.kind === 'html' || name.toLowerCase().includes('html') || ['body_html', 'update_body', 'accounts_html'].includes(name);

const AUDIENCE_OPTIONS = [
  { value: 'all_dealers', label: 'All Dealers' },
  { value: 'installation_center', label: 'Installation Centers' },
  { value: 'elite_partner', label: 'Elite Partners' },
  { value: 'sales_rep', label: 'Sales Reps' },
  { value: 'all_partners', label: 'Everyone (dealers + reps)' },
  { value: 'all_admins', label: 'Admins' },
  { value: 'custom', label: 'Specific emails' },
];
const audLabel = (v) => AUDIENCE_OPTIONS.find(o => o.value === v)?.label || v;

const catColor = (c) => ({ system: '#3B82F6', transactional: '#10B981', marketing: '#8B5CF6', custom: '#64748B' }[c] || '#64748B');

const Modal = ({ open, onClose, title, subtitle, icon: Icon, children, testId, maxW = 'max-w-3xl' }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className={`bg-[#101317] border border-white/10 rounded-2xl w-full ${maxW} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="flex items-start justify-between p-5 border-b border-white/[0.06] sticky top-0 bg-[#101317] z-10">
          <div className="flex items-center gap-3">
            {Icon && <div className="w-9 h-9 rounded-xl bg-[#C6A15B]/15 flex items-center justify-center"><Icon className="w-5 h-5 text-[#C6A15B]" /></div>}
            <div><h3 className="text-base font-semibold text-slate-100">{title}</h3>{subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

/* ================================ QUICK SEND ================================ */
const QuickSend = () => {
  const [quick, setQuick] = useState({ subject: '', message: '', audience: 'all_dealers', custom_emails: '' });
  const [count, setCount] = useState({ count: 0, preview: [] });
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    if (quick.audience === 'custom') return;
    axios.get(`${API}/api/admin/email-audience-count?audience=${quick.audience}`)
      .then(res => setCount({ count: res.data.count, preview: res.data.preview }))
      .catch(() => setCount({ count: 0, preview: [] }));
  }, [quick.audience]);

  const validate = () => {
    if (!quick.subject.trim()) { toast.error('Subject is required'); return false; }
    if (!quick.message.trim()) { toast.error('Message is required'); return false; }
    if (quick.audience === 'custom' && !quick.custom_emails.trim()) { toast.error('Enter at least one email'); return false; }
    return true;
  };

  const doSend = async () => {
    setSending(true); setConfirm(false);
    try {
      const payload = { subject: quick.subject, message: quick.message, audience: quick.audience };
      if (quick.audience === 'custom') payload.custom_emails = quick.custom_emails.split(',').map(e => e.trim()).filter(Boolean);
      const res = await axios.post(`${API}/api/admin/email-quick-send`, payload);
      const { status, sent_count, failed_count } = res.data;
      if (status === 'sent') toast.success(`Sent to ${sent_count} recipient(s)`);
      else if (status === 'partial') toast.warning(`Sent ${sent_count}, ${failed_count} failed`);
      else toast.error('Send failed');
      if (status !== 'failed') setQuick({ ...quick, subject: '', message: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  const customCount = quick.audience === 'custom' ? quick.custom_emails.split(',').map(e => e.trim()).filter(Boolean).length : count.count;

  return (
    <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-6 space-y-4" data-testid="quick-composer">
      <div>
        <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2"><Zap className="w-5 h-5 text-[#C6A15B]" /> Compose a Message</h3>
        <p className="text-slate-400 text-sm mt-1">Type a subject and message, pick who gets it, and send — automatically styled in TC branding.</p>
      </div>

      <div>
        <label className={labelCls}>Send to</label>
        <select className={inputCls} value={quick.audience} onChange={(e) => setQuick({ ...quick, audience: e.target.value })} data-testid="quick-audience-select">
          {AUDIENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {quick.audience === 'custom' ? (
          <input className={`${inputCls} mt-2`} placeholder="alice@example.com, bob@example.com" value={quick.custom_emails} onChange={(e) => setQuick({ ...quick, custom_emails: e.target.value })} data-testid="quick-custom-emails" />
        ) : (
          <p className="text-xs text-[#C6A15B] mt-1.5" data-testid="quick-recipient-count">{count.count} recipient(s){count.preview?.length ? ` · ${count.preview.join(', ')}${count.count > 5 ? '…' : ''}` : ''}</p>
        )}
      </div>

      <div><label className={labelCls}>Subject</label><input className={inputCls} placeholder="e.g. New winter pricing is live" value={quick.subject} onChange={(e) => setQuick({ ...quick, subject: e.target.value })} data-testid="quick-subject-input" /></div>
      <div>
        <label className={labelCls}>Message</label>
        <textarea rows={8} className={inputCls} placeholder="Write your message here. Leave a blank line between paragraphs." value={quick.message} onChange={(e) => setQuick({ ...quick, message: e.target.value })} data-testid="quick-message-input" />
        <p className="text-xs text-slate-500 mt-1">Plain text — no codes needed. TC branding and layout are added automatically.</p>
      </div>

      <div className="flex justify-end">
        <button onClick={() => { if (validate()) setConfirm(true); }} disabled={sending} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#C6A15B] hover:bg-[#b5924f] text-black text-sm font-semibold" data-testid="quick-send-btn">
          <Send className="w-4 h-4" /> {sending ? 'Sending…' : 'Review & Send'}
        </button>
      </div>

      <Modal open={confirm} onClose={() => setConfirm(false)} title="Confirm Send" subtitle="This sends a real email to the selected group." icon={AlertTriangle} testId="quick-confirm-dialog" maxW="max-w-md">
        <div className="space-y-4">
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4 text-sm text-slate-300">
            You're about to email <span className="font-semibold text-[#C6A15B]">{customCount}</span> recipient(s) in <span className="font-semibold text-slate-100">{audLabel(quick.audience)}</span>.
          </div>
          <div className="bg-black/40 border border-white/10 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-0.5">Subject</p>
            <p className="text-sm text-slate-100">{quick.subject}</p>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setConfirm(false)} className="px-4 py-2 rounded-lg border border-white/10 text-sm text-slate-300">Cancel</button>
            <button onClick={doSend} disabled={sending} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#C6A15B] hover:bg-[#b5924f] text-black text-sm font-semibold" data-testid="quick-confirm-send">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send Now</>}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

/* ================================ TEMPLATES ================================ */
const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', category: 'custom', description: '', subject: '', html_body: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ template_id: '', name: '', category: 'custom', description: '', subject: '', html_body: '' });

  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewVarsText, setPreviewVarsText] = useState('{}');
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);

  const [showBroadcast, setShowBroadcast] = useState(false);
  const [bAudience, setBAudience] = useState('all_admins');
  const [bCustom, setBCustom] = useState('');
  const [bVars, setBVars] = useState({});
  const [bSubject, setBSubject] = useState('');
  const [bCount, setBCount] = useState({ count: 0, preview: [] });
  const [bSending, setBSending] = useState(false);
  const [bConfirm, setBConfirm] = useState(false);

  const fetchTemplates = async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/api/admin/email-templates`); setTemplates(res.data.templates || []); }
    catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchTemplates(); }, []);
  useEffect(() => {
    if (!showBroadcast || bAudience === 'custom') return;
    axios.get(`${API}/api/admin/email-audience-count?audience=${bAudience}`)
      .then(res => setBCount({ count: res.data.count, preview: res.data.preview }))
      .catch(() => setBCount({ count: 0, preview: [] }));
  }, [bAudience, showBroadcast]);

  const openEdit = (tpl) => { setSelected(tpl); setEditForm({ name: tpl.name, category: tpl.category, description: tpl.description || '', subject: tpl.subject, html_body: tpl.html_body }); setShowEdit(true); };
  const saveEdit = async () => {
    try { await axios.put(`${API}/api/admin/email-templates/${selected.template_id}`, editForm); toast.success('Template saved'); setShowEdit(false); fetchTemplates(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const createTemplate = async () => {
    try {
      await axios.post(`${API}/api/admin/email-templates`, createForm);
      toast.success('Template created'); setShowCreate(false);
      setCreateForm({ template_id: '', name: '', category: 'custom', description: '', subject: '', html_body: '' }); fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
  };
  const resetTpl = async (tpl) => {
    if (!window.confirm('Reset this template to its built-in default?')) return;
    try { await axios.post(`${API}/api/admin/email-templates/${tpl.template_id}/reset`); toast.success('Template reset'); fetchTemplates(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Reset failed'); }
  };
  const deleteTpl = async (tpl) => {
    if (!window.confirm('Delete this template permanently?')) return;
    try { await axios.delete(`${API}/api/admin/email-templates/${tpl.template_id}`); toast.success('Template deleted'); fetchTemplates(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const openPreview = async (tpl, extraVars = null) => {
    setSelected(tpl);
    let vars = {};
    if (extraVars) vars = extraVars; else { try { vars = JSON.parse(previewVarsText || '{}'); } catch { vars = {}; } }
    try { const res = await axios.post(`${API}/api/admin/email-templates/${tpl.template_id}/preview`, { variables: vars }); setPreviewHtml(res.data.html); setPreviewSubject(res.data.subject); setShowPreview(true); }
    catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
  };
  const sendTest = async () => {
    if (!selected || !testEmail) { toast.error('Enter a test email'); return; }
    setTestSending(true);
    try {
      let vars = {}; try { vars = JSON.parse(previewVarsText || '{}'); } catch { vars = {}; }
      const res = await axios.post(`${API}/api/admin/email-templates/${selected.template_id}/test-send`, { to_email: testEmail, variables: vars });
      if (res.data.ok) toast.success(`Test email sent to ${testEmail}`); else toast.error(res.data.result?.error || 'Test send failed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setTestSending(false); }
  };

  const openBroadcast = (tpl) => {
    setSelected(tpl);
    const vars = {}; (tpl.variables || []).forEach(v => { vars[v.name] = ''; });
    setBVars(vars); setBSubject(''); setBCustom(''); setBAudience('all_admins'); setShowBroadcast(true);
  };
  const doBroadcast = async () => {
    if (!selected) return;
    setBSending(true); setBConfirm(false);
    try {
      const payload = { template_id: selected.template_id, audience: bAudience, variables: bVars };
      if (bSubject) payload.subject_override = bSubject;
      if (bAudience === 'custom') payload.custom_emails = bCustom.split(',').map(e => e.trim()).filter(Boolean);
      const res = await axios.post(`${API}/api/admin/email-broadcast`, payload);
      const { sent_count, failed_count, status } = res.data;
      if (status === 'sent') toast.success(`Broadcast sent to ${sent_count}`);
      else if (status === 'partial') toast.warning(`Sent ${sent_count}, ${failed_count} failed`);
      else toast.error('Broadcast failed');
      setShowBroadcast(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Broadcast failed'); }
    finally { setBSending(false); }
  };

  const bRecipients = bAudience === 'custom' ? bCustom.split(',').map(e => e.trim()).filter(Boolean).length : bCount.count;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">Reusable branded templates for broadcasts and automated emails.</p>
        <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-[#C6A15B] hover:bg-[#b5924f] text-black text-sm font-semibold" data-testid="new-template-btn"><Plus className="w-4 h-4" /> New Template</button>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 gap-4">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-44 w-full" />)}</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {templates.map(tpl => {
            const userVars = (tpl.variables || []).filter(v => !AUTO_FILLED_VARS.has(v.name));
            return (
              <div key={tpl.template_id} className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-5 flex flex-col" data-testid={`template-card-${tpl.template_id}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-semibold text-slate-100">{tpl.name}</h3>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md" style={{ background: `${catColor(tpl.category)}22`, color: catColor(tpl.category) }}>{tpl.category}</span>
                    {tpl.is_system && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-[#C6A15B]/20 text-[#C6A15B]">system</span>}
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 font-mono mb-2">{tpl.template_id}</p>
                {tpl.description && <p className="text-sm text-slate-400 mb-2">{tpl.description}</p>}
                <div className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 mb-3">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Subject</p>
                  <p className="text-sm text-slate-200">{tpl.subject}</p>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3 min-h-[1.5rem]">
                  {userVars.length === 0 ? <span className="text-xs text-slate-500">No custom fields — pick an audience and send.</span> : (
                    <>{userVars.slice(0, 6).map(v => <span key={v.name} className="text-[11px] px-2 py-0.5 rounded border border-[#C6A15B]/30 bg-black/40 text-slate-300">{getVarLabel(v.name, v.description)}</span>)}
                      {userVars.length > 6 && <span className="text-xs text-slate-500">+{userVars.length - 6} more</span>}</>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 pt-3 mt-auto border-t border-white/[0.06]">
                  <button onClick={() => openEdit(tpl)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200" data-testid={`edit-template-${tpl.template_id}`}><Edit className="w-3 h-3" /> Edit</button>
                  <button onClick={() => openPreview(tpl)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200" data-testid={`preview-template-${tpl.template_id}`}><Eye className="w-3 h-3" /> Preview</button>
                  <button onClick={() => openBroadcast(tpl)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`broadcast-template-${tpl.template_id}`}><Send className="w-3 h-3" /> Broadcast</button>
                  {tpl.is_system ? (
                    <button onClick={() => resetTpl(tpl)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200"><RotateCcw className="w-3 h-3" /> Reset</button>
                  ) : (
                    <button onClick={() => deleteTpl(tpl)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10"><Trash2 className="w-3 h-3" /> Delete</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit */}
      <Modal open={showEdit} onClose={() => setShowEdit(false)} title={`Edit Template: ${selected?.name || ''}`} subtitle="Placeholders like {{first_name}} auto-fill per recipient." icon={Edit} testId="edit-template-dialog" maxW="max-w-4xl">
        <div className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className={labelCls}>Name</label><input className={inputCls} value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div><label className={labelCls}>Category</label>
              <select className={inputCls} value={editForm.category} onChange={e => setEditForm({ ...editForm, category: e.target.value })}>
                <option value="system">system</option><option value="transactional">transactional</option><option value="marketing">marketing</option><option value="custom">custom</option>
              </select>
            </div>
          </div>
          <div><label className={labelCls}>Description</label><input className={inputCls} value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} /></div>
          <div><label className={labelCls}>Subject</label><input className={`${inputCls} font-mono text-sm`} value={editForm.subject} onChange={e => setEditForm({ ...editForm, subject: e.target.value })} data-testid="edit-subject" /></div>
          <div>
            <label className={labelCls}>Email Body</label>
            <div className="bg-white rounded" data-testid="edit-html-body">
              <ReactQuill theme="snow" value={editForm.html_body} onChange={(html) => setEditForm({ ...editForm, html_body: html })} modules={quillModules} formats={quillFormats} style={{ height: 320, marginBottom: 42 }} />
            </div>
            <p className="text-xs text-slate-500 mt-1">Use placeholders like {'{{first_name}}'} — they auto-fill per recipient.</p>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-white/[0.06]">
            <button onClick={() => setShowEdit(false)} className="px-4 py-2 rounded-lg border border-white/10 text-sm text-slate-300">Cancel</button>
            <button onClick={() => openPreview(selected, {})} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-slate-200"><Eye className="w-4 h-4" /> Preview</button>
            <button onClick={saveEdit} className="px-4 py-2 rounded-lg bg-[#C6A15B] hover:bg-[#b5924f] text-black text-sm font-semibold" data-testid="save-template-btn">Save</button>
          </div>
        </div>
      </Modal>

      {/* Create */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Template" icon={Plus} maxW="max-w-3xl">
        <div className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className={labelCls}>Template ID *</label><input className={`${inputCls} font-mono`} placeholder="my_custom_email" value={createForm.template_id} onChange={e => setCreateForm({ ...createForm, template_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} /></div>
            <div><label className={labelCls}>Name *</label><input className={inputCls} value={createForm.name} onChange={e => setCreateForm({ ...createForm, name: e.target.value })} /></div>
          </div>
          <div><label className={labelCls}>Category</label>
            <select className={inputCls} value={createForm.category} onChange={e => setCreateForm({ ...createForm, category: e.target.value })}>
              <option value="custom">custom</option><option value="marketing">marketing</option><option value="transactional">transactional</option><option value="system">system</option>
            </select>
          </div>
          <div><label className={labelCls}>Description</label><input className={inputCls} value={createForm.description} onChange={e => setCreateForm({ ...createForm, description: e.target.value })} /></div>
          <div><label className={labelCls}>Subject *</label><input className={`${inputCls} font-mono`} value={createForm.subject} onChange={e => setCreateForm({ ...createForm, subject: e.target.value })} /></div>
          <div>
            <label className={labelCls}>Email Body *</label>
            <div className="bg-white rounded"><ReactQuill theme="snow" value={createForm.html_body} onChange={(html) => setCreateForm({ ...createForm, html_body: html })} modules={quillModules} formats={quillFormats} style={{ height: 280, marginBottom: 42 }} placeholder="<h1>Hello {{first_name}}</h1><p>Your body here</p>" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-white/[0.06]">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg border border-white/10 text-sm text-slate-300">Cancel</button>
            <button onClick={createTemplate} className="px-4 py-2 rounded-lg bg-[#C6A15B] hover:bg-[#b5924f] text-black text-sm font-semibold">Create</button>
          </div>
        </div>
      </Modal>

      {/* Preview + test send */}
      <Modal open={showPreview} onClose={() => setShowPreview(false)} title="Template Preview" icon={Eye} testId="preview-dialog" maxW="max-w-4xl">
        <div className="space-y-3">
          <div className="bg-black/40 border border-white/10 rounded-lg p-3"><p className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Subject</p><p className="text-slate-100 font-medium">{previewSubject}</p></div>
          <div>
            <label className={labelCls}>Sample variables (JSON)</label>
            <textarea rows={2} className={`${inputCls} font-mono text-xs`} value={previewVarsText} onChange={e => setPreviewVarsText(e.target.value)} placeholder='{"first_name":"Alex"}' />
            <button onClick={() => openPreview(selected)} className="mt-1.5 text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200">Re-render</button>
          </div>
          <div className="border border-white/10 rounded bg-white"><iframe title="preview" srcDoc={previewHtml} style={{ width: '100%', height: 480, border: 0, borderRadius: 4 }} data-testid="preview-iframe" /></div>
          <div className="pt-3 border-t border-white/[0.06] bg-[#C6A15B]/5 -mx-5 -mb-5 p-5 rounded-b-2xl">
            <label className={labelCls}>Test Send — delivers one email to you only</label>
            <div className="flex gap-2">
              <input type="email" className={`${inputCls} flex-1`} placeholder="you@example.com" value={testEmail} onChange={e => setTestEmail(e.target.value)} data-testid="test-email-input" />
              <button onClick={sendTest} disabled={testSending} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-slate-200" data-testid="send-test-btn">{testSending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Test'}</button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Broadcast */}
      <Modal open={showBroadcast} onClose={() => setShowBroadcast(false)} title={`Broadcast: ${selected?.name || ''}`} subtitle="Send this template to a recipient group." icon={Send} testId="broadcast-dialog">
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Audience</label>
            <select className={inputCls} value={bAudience} onChange={e => setBAudience(e.target.value)} data-testid="broadcast-audience-select">
              {AUDIENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <p className="text-xs text-emerald-400 mt-1.5" data-testid="audience-count">{bAudience === 'custom' ? 'Enter emails below' : `${bCount.count} recipient(s) · ${(bCount.preview || []).join(', ')}${bCount.count > 5 ? '…' : ''}`}</p>
          </div>
          {bAudience === 'custom' && <div><label className={labelCls}>Emails (comma separated)</label><textarea rows={2} className={inputCls} value={bCustom} onChange={e => setBCustom(e.target.value)} placeholder="alice@example.com, bob@example.com" data-testid="custom-emails-input" /></div>}
          <div className="bg-[#C6A15B]/5 border border-[#C6A15B]/20 rounded-lg p-3 text-xs text-slate-300"><span className="text-[#C6A15B] font-semibold">Tip:</span> Fill the fields below like a normal email — each recipient's name auto-fills.</div>
          <div><label className={labelCls}>Subject override (optional)</label><input className={inputCls} value={bSubject} onChange={e => setBSubject(e.target.value)} placeholder={selected?.subject || 'Leave blank to use template default'} /></div>
          {(selected?.variables || []).filter(v => !AUTO_FILLED_VARS.has(v.name)).map(v => (
            <div key={v.name}>
              <label className={labelCls}>{getVarLabel(v.name, v.description)}</label>
              {isHtmlVar(v.name) ? (
                <div className="bg-white rounded"><ReactQuill theme="snow" value={bVars[v.name] || ''} onChange={(html) => setBVars({ ...bVars, [v.name]: html })} modules={quillModules} formats={quillFormats} style={{ height: 180, marginBottom: 42 }} placeholder={getVarPlaceholder(v.name, v.description)} /></div>
              ) : (
                <input className={inputCls} value={bVars[v.name] || ''} onChange={e => setBVars({ ...bVars, [v.name]: e.target.value })} placeholder={getVarPlaceholder(v.name, v.description)} data-testid={`broadcast-var-${v.name}`} />
              )}
            </div>
          ))}
          <div className="flex justify-end gap-2 pt-3 border-t border-white/[0.06]">
            <button onClick={() => setShowBroadcast(false)} className="px-4 py-2 rounded-lg border border-white/10 text-sm text-slate-300">Cancel</button>
            <button onClick={() => { if (bAudience !== 'custom' && bCount.count === 0) { toast.error('No recipients in this audience'); return; } if (bAudience === 'custom' && bRecipients === 0) { toast.error('Enter at least one email'); return; } setBConfirm(true); }} disabled={bSending} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold" data-testid="send-broadcast-btn">{bSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send Broadcast ({bRecipients})</>}</button>
          </div>
        </div>
      </Modal>

      <Modal open={bConfirm} onClose={() => setBConfirm(false)} title="Confirm Broadcast" icon={AlertTriangle} maxW="max-w-md">
        <div className="space-y-4">
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4 text-sm text-slate-300">Send <span className="font-semibold text-slate-100">{selected?.name}</span> to <span className="font-semibold text-[#C6A15B]">{bRecipients}</span> recipient(s) in <span className="font-semibold text-slate-100">{audLabel(bAudience)}</span>?</div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setBConfirm(false)} className="px-4 py-2 rounded-lg border border-white/10 text-sm text-slate-300">Cancel</button>
            <button onClick={doBroadcast} disabled={bSending} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold">{bSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send Now</>}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

/* ================================ AUTOMATIONS ================================ */
const AUTOMATIONS = [
  { icon: UserPlus, color: '#3B82F6', title: 'Staff Welcome Email', trigger: 'When a new staff/dealer account is created', detail: 'Sends the welcome_dealer template with the new login username and portal URL. Customer accounts created for the portal do NOT get this — they receive a secure invitation instead.' },
  { icon: KeyRound, color: '#C6A15B', title: 'Portal Invitation', trigger: 'When an admin/rep invites a customer to the portal', detail: 'Sends a single-use, hashed, 7-day activation link (never a password). Resending invalidates the prior link. This security flow is managed in Clients — not editable here.' },
  { icon: BellRing, color: '#F59E0B', title: 'Software Renewal Reminders', trigger: 'Daily digest to admins (once per day)', detail: 'Emails admins a digest of expired / expiring software subscriptions. Can be triggered manually below.' },
];

const Automations = () => {
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(true);

  const fetchStatus = async () => { try { const res = await axios.get(`${API}/api/admin/software-accounts/reminder-status`); setStatus(res.data); } catch { /* ignore */ } };
  const fetchLog = async () => { setLoadingLog(true); try { const res = await axios.get(`${API}/api/admin/email-log?limit=30`); setLog(res.data.logs || []); } catch { /* ignore */ } finally { setLoadingLog(false); } };
  useEffect(() => { fetchStatus(); fetchLog(); }, []);

  const sendReminders = async () => {
    setSending(true);
    try { const res = await axios.post(`${API}/api/admin/software-accounts/send-renewal-reminders?force=true`); toast.success(`Renewal digest sent (${res.data.sent_count ?? res.data.recipients_count ?? 0} admin recipient(s))`); fetchStatus(); fetchLog(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not send reminders'); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-5">
      <div className="bg-[#C6A15B]/5 border border-[#C6A15B]/20 rounded-xl p-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-[#C6A15B] flex-shrink-0 mt-0.5" />
        <p className="text-sm text-slate-300">These emails are sent automatically by the system. Their triggers are built into the platform and aren't edited here — but the templates they use can be customized in the <span className="text-[#C6A15B] font-medium">Templates</span> tab.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {AUTOMATIONS.map((a) => (
          <div key={a.title} className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-5">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ background: `${a.color}1f` }}><a.icon className="w-5 h-5" style={{ color: a.color }} /></div>
            <h3 className="text-base font-semibold text-slate-100 mb-1">{a.title}</h3>
            <p className="text-xs text-[#C6A15B] mb-2">{a.trigger}</p>
            <p className="text-sm text-slate-400 leading-relaxed">{a.detail}</p>
          </div>
        ))}
      </div>

      <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-base font-semibold text-slate-100 flex items-center gap-2"><BellRing className="w-5 h-5 text-amber-400" /> Renewal Reminder Digest</h3>
            <p className="text-sm text-slate-400 mt-1">Last sent: {status?.last_sent_at ? new Date(status.last_sent_at).toLocaleString() : (status?.last_sent_date || 'never')}</p>
          </div>
          <button onClick={sendReminders} disabled={sending} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-slate-200" data-testid="send-reminders-btn">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send Now</>}</button>
        </div>
      </div>

      <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-5">
        <h3 className="text-base font-semibold text-slate-100 flex items-center gap-2 mb-3"><History className="w-5 h-5 text-slate-400" /> Recent Email Activity</h3>
        {loadingLog ? <Skeleton className="h-24 w-full" /> : log.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">No emails sent yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500 border-b border-white/[0.06]"><th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Type</th><th className="py-2 pr-3">Template / Subject</th><th className="py-2 pr-3">Audience / To</th><th className="py-2 text-right">Sent / Failed</th></tr></thead>
              <tbody>
                {log.map(l => (
                  <tr key={l.log_id} className="border-b border-white/[0.04]">
                    <td className="py-2 pr-3 text-slate-400 text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="py-2 pr-3"><span className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-slate-300">{l.kind}</span></td>
                    <td className="py-2 pr-3 text-slate-300 text-xs font-mono">{l.template_id || (l.subject_override ? `“${l.subject_override}”` : '—')}</td>
                    <td className="py-2 pr-3 text-slate-400 text-xs">{l.audience || l.to_email}</td>
                    <td className="py-2 text-right text-xs"><span className="text-emerald-400">{l.sent_count ?? (l.result?.ok ? 1 : 0)}</span> / <span className="text-red-400">{l.failed_count ?? (l.result?.ok ? 0 : 1)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

/* ================================ SHELL ================================ */
export const EmailCenter = () => {
  const [tab, setTab] = useState('quick');
  const TABS = [
    { k: 'quick', label: 'Quick Send', icon: Zap },
    { k: 'templates', label: 'Templates', icon: LayoutTemplate },
    { k: 'automations', label: 'Automations', icon: BellRing },
  ];
  return (
    <div className="space-y-6" data-testid="email-center-section">
      <div>
        <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-2.5"><Mail className="w-6 h-6 text-[#C6A15B]" /> Email Center</h2>
        <p className="text-sm text-slate-400 mt-1">Send announcements, manage branded templates, and review automated emails.</p>
      </div>

      <div className="flex gap-1 border-b border-white/[0.06]">
        {TABS.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.k ? 'border-[#C6A15B] text-[#C6A15B]' : 'border-transparent text-slate-400 hover:text-slate-200'}`} data-testid={`email-tab-${t.k}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'quick' && <QuickSend />}
      {tab === 'templates' && <Templates />}
      {tab === 'automations' && <Automations />}
    </div>
  );
};

export default EmailCenter;
